import React from 'react';
import {
    Card,
    Details,
    DetailsStringValue,
    DetailsReactNodeValue,
    DetailsArrayValue,
    AddressLink,
    AdvancedTable,
    ExamApplicationStatus,
    renderSignedExpertsRow,
} from 'components';
import { createExamDate, Color } from '_helpers';
import { ExamInfoTabProps } from '../ExamInfoTab';
import { IApplicantsTabProps } from '../ApplicantsTab';
import { IProps as IExamDetailsTabProps } from '../ExamDetailsTabs';
import styles from './NokOrAkProtocolTab.module.scss';
import cn from 'classnames';

const NokOrAkProtocolTab = ({
    examDetails,
    examApplicationsList,
    handleApplicantsListChange,
    asAkProtocol,
}: IProps) => {
    const applicationsHeader = [
        { fieldName: 'applicantFullName', title: 'ФАМИЛИЯ, ИМЯ, ОТЧЕСТВО' },
        { fieldName: 'status', title: 'Дата рождения' },
        { fieldName: 'status', title: '% верных в теории' },
        { fieldName: 'status', title: 'Практика' },
        { fieldName: 'status', title: 'Результат' },
    ];

    const createTableData = examApplicationsList && examApplicationsList.items.filter(a => a.status === ExamApplicationStatus.Passed || a.status === ExamApplicationStatus.NotPassed).map(({
        examApplicationId, applicantFullName, status, birthDate, correctTheoreticalAnswersPercentage,
    }) => ({
        id: examApplicationId,
        columns: [
            applicantFullName,
            birthDate.format('DD.MM.YYYY'),
            correctTheoreticalAnswersPercentage || 0,
            status === ExamApplicationStatus.Passed ? 'Сдана' : `${status === ExamApplicationStatus.Canceled ? 'Нет допуска' : 'Не сдана'}`,
            <span
              style={{ color: `var(${status === ExamApplicationStatus.Passed ? Color.Success : Color.Red})` }}
            >
                {status === ExamApplicationStatus.Passed ? 'Экзамен сдан' : 'Экзамен не сдан'}
            </span>,
        ],
    }));

    const renderExperts = (experts: ExamInfoTabProps['examDetails']['experts']) => experts.map(expert => (
        <span key={`expert_${expert.expertId} `} className="d-block mb-2">
            {expert.chairman ? 'Председатель экспертной комиссии:' : 'Член экспертной комиссии:'} <strong>{expert.fullName}</strong>
        </span>
    ));

    const getInfoRowsFromExamDetails = () => {
        if (!asAkProtocol) {
            const infoRows: (DetailsReactNodeValue | DetailsStringValue | DetailsArrayValue)[] = [
                new DetailsStringValue('Цок', examDetails.examinationCenterName),
                new DetailsStringValue('Номер протокола', examDetails.protocolNumber ? examDetails.protocolNumber.toString() : ''),
                new DetailsStringValue('Дата заседания', (examDetails.protocolDate ? examDetails.protocolDate.format('DD.MM.YYYY') : '')),
                new DetailsStringValue('Дата экзамена', examDetails.date.format('DD.MM.YYYY')),
                new DetailsStringValue('Время проведения', createExamDate(examDetails.actualStartTime || examDetails.date)),
                new DetailsReactNodeValue('Место проведения', <AddressLink city={examDetails.city} address={examDetails.address} withoutLink />),
                new DetailsReactNodeValue('Комиссия', renderExperts(examDetails.experts)),
            ];

            return infoRows;
        }

        const infoRows: (DetailsReactNodeValue | DetailsStringValue | DetailsArrayValue)[] = [
            new DetailsStringValue('Номер протокола', examDetails.protocolNumber ? examDetails.protocolNumber.toString() : ''),
            new DetailsStringValue('Город', examDetails.city),
            new DetailsStringValue('Дата заседания', (examDetails.protocolDate ? examDetails.protocolDate.format('DD.MM.YYYY') : '')),
            new DetailsReactNodeValue('Комиссия', examDetails.akExperts.filter(expert => expert.active).map(expert => <span key={`expert_${expert.expertId}`} className="d-block mb-2">{expert.fullName}</span>)),
            new DetailsStringValue('Решили', `Рассмотрев результаты независимой оценки квалификации в форме профессионального
            экзамена комиссия единогласно постановила:
            Признать результаты независимой оценки квалификации в форме профессионального
            экзамена: ${examDetails.numberOfApplicants} профессиональных экзамена - «${examDetails.qualificationTitle}»`),
            new DetailsStringValue('Дата экзамена', examDetails.date.format('DD.MM.YYYY')),
            new DetailsStringValue('Время проведения', createExamDate(examDetails.actualStartTime || examDetails.date)),
            new DetailsReactNodeValue('Место проведения', <AddressLink city={examDetails.city} address={examDetails.address} withoutLink />),

        ];

        return infoRows;
    };

    const renderProtocols = () => (
        <div className={styles.protocolsContainer}>
            {renderSignedExpertsRow(asAkProtocol
                ? examDetails.akExperts.filter(expert => expert.active)
                : examDetails.experts.filter(expert => expert.active),
                asAkProtocol ? 'аттестационной комиссии' : 'экспертной комиссии')
            }
        </div>
    );

    const getSignedRowsFromExamDetails = () => {
        const infoRows: (DetailsReactNodeValue | DetailsStringValue | DetailsArrayValue)[] = [
            new DetailsReactNodeValue('Подписи', renderProtocols()),
        ];

        return infoRows;
    };

    return (
        <Card
          title={!asAkProtocol ? 'Подписание протокола НОК' : 'Подписание протокола аттестационной комиссии'}
          category={!asAkProtocol ? 'Необходимо подписать электронный протокол экспертной комиссии' : 'Необходимо подписать электронный протокол аттестационной комиссии'}
          contentClassName="pt-0"
          cardClassName="pt-0"
          withoutBorder
          withoutPadding
        >
            <div className={styles.detailsContainer}>
                <Details infoRows={getInfoRowsFromExamDetails()} withDarkRowLabel />
            </div>

            {!asAkProtocol ? (
                <AdvancedTable
                  data={createTableData || []}
                  tableHeaders={applicationsHeader}
                  handlePageChange={({ page, perPage }) => handleApplicantsListChange(undefined, undefined, page, perPage)}
                  handleSearch={handleApplicantsListChange}
                  handleOrder={() => true}
                  handleRowClick={() => true}
                  page={(examApplicationsList && examApplicationsList.page) || 1}
                  pageSize={(examApplicationsList && examApplicationsList.pageSize) || 10}
                  total={(examApplicationsList && examApplicationsList.total) || 1}
                />
            ) : null}

            <div className={cn(styles.detailsContainer, styles.protocolsDetailsContainer)}>
                <Details infoRows={getSignedRowsFromExamDetails()} withDarkRowLabel />
            </div>
        </Card>
    );
};

export default NokOrAkProtocolTab;

interface IProps {
    examDetails: ExamInfoTabProps['examDetails'];
    examApplicationsList: IExamDetailsTabProps['examApplicationsList'];
    handleApplicantsListChange: IApplicantsTabProps['handleApplicantsListChange'];
    asAkProtocol?: boolean;
}
